import { c as _c } from "react/compiler-runtime";
import Badge from '@precis-digital/kurama/Badge';
import Button, { type ButtonProps } from '@precis-digital/kurama/Button';
import Chip from '@precis-digital/kurama/Chip';
import Grid from '@precis-digital/kurama/Grid';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { isEmpty } from 'ramda';
import React, { isValidElement, type ReactNode } from 'react';
import { Controller, type Control, type ControllerRenderProps, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useHasAccess } from 'shared/hooks/useHasAccess';
import { type InputRules } from 'shared/reactHookForm';
import { useTranslation } from 'shared/translations';
import { useQueryMember } from 'src/workspace/api';
import FormButtons from './FormButtons';
import FormLabel from './FormLabel';
const EditItem = t0 => {
  const $ = _c(22);
  const {
    label,
    currentValue,
    canEdit: t1,
    hasEditAccess,
    onEditClick,
    editLabel,
    editIcon,
    editButtonVariant: t2,
    errorMessage
  } = t0;
  const canEdit = t1 === undefined ? false : t1;
  const editButtonVariant = t2 === undefined ? "text" : t2;
  const {
    t
  } = useTranslation("common");
  let t3;
  if ($[0] !== label) {
    t3 = <label>{label}</label>;
    $[0] = label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== currentValue || $[3] !== t) {
    t4 = Array.isArray(currentValue) && currentValue.length > 0 ? <Grid container={true} gap="6px">{currentValue.map(_temp)}</Grid> : <Typography variant="body2">{currentValue != null && !isEmpty(currentValue) ? isValidElement(currentValue) ? currentValue : currentValue.toString() : t("form.notSet")}</Typography>;
    $[2] = currentValue;
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== errorMessage) {
    t5 = errorMessage != null && <StyledErrorMessage><Badge color="error" /><Typography variant="body3">{errorMessage}</Typography></StyledErrorMessage>;
    $[5] = errorMessage;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <StyledItem xs={true} display="flex" flexDirection="column" gap="4px">{t3}{t4}{t5}</StyledItem>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== canEdit || $[12] !== editButtonVariant || $[13] !== editIcon || $[14] !== editLabel || $[15] !== hasEditAccess || $[16] !== onEditClick || $[17] !== t) {
    t7 = canEdit && <Button {...hasEditAccess === false && {
      tooltipProps: {
        kind: "singleline",
        title: t("noPermissionMessage")
      }
    }} disabled={hasEditAccess === false} variant={editButtonVariant} onClick={onEditClick} rightIcon={editIcon}>{editLabel ?? t("form.edit")}</Button>;
    $[11] = canEdit;
    $[12] = editButtonVariant;
    $[13] = editIcon;
    $[14] = editLabel;
    $[15] = hasEditAccess;
    $[16] = onEditClick;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <Grid display="flex" gap="24px" alignItems="center" alignSelf="stretch">{t6}{t7}</Grid>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
export type FormItemCurrentValue = string | string[] | number | Date | boolean | React.ReactElement | Array<string | React.ReactElement>;
interface BaseFormItemProps<FormData extends FieldValues, T extends FieldPath<FormData>> {
  label?: string;
  labelIcon?: string;
  labelChip?: ReactNode;
  helperText?: ReactNode;
  name: T;
  control: Control<FormData>;
  rules?: InputRules<FormData, T>;
  render: ({
    field
  }: {
    field: ControllerRenderProps<FormData, T>;
  }) => React.ReactElement;
  isCreateEvent: boolean;
  defaultValue?: PathValue<FormData, T>;
  currentValue?: FormItemCurrentValue;
  selectedLabel?: React.ReactNode;
  xs?: number | boolean;
  editModes?: Record<string, boolean>;
  handleUpdate?: (name: T, callback: () => void) => void;
  toggleEdit?: (name: T) => void;
  onCancelEdit?: () => void;
  isSaveDisabled?: boolean;
  renderLabel?: (labelText: React.ReactNode) => React.ReactNode;
  renderSecondaryLabel?: (secondaryLabel: React.ReactNode) => React.ReactNode;
  editLabel?: string;
  editIcon?: ReactNode;
  editButtonVariant?: ButtonProps['variant'];
  errorMessage?: string;
}
interface NewFormItem<FormData extends FieldValues, T extends FieldPath<FormData>> extends BaseFormItemProps<FormData, T> {
  canEdit?: false;
}
interface EditableFormItem<FormData extends FieldValues, T extends FieldPath<FormData>> extends BaseFormItemProps<FormData, T> {
  canEdit?: true;
  editModes: BaseFormItemProps<FormData, T>['editModes'];
  handleUpdate: BaseFormItemProps<FormData, T>['handleUpdate'];
  toggleEdit: BaseFormItemProps<FormData, T>['toggleEdit'];
}
const FormItem = t0 => {
  const $ = _c(27);
  const {
    label,
    labelIcon,
    labelChip,
    helperText,
    name,
    control,
    rules,
    render,
    isCreateEvent,
    currentValue,
    selectedLabel,
    xs: t1,
    defaultValue,
    canEdit,
    isSaveDisabled: t2,
    editModes,
    handleUpdate,
    toggleEdit,
    onCancelEdit,
    renderLabel,
    renderSecondaryLabel,
    editLabel,
    editIcon,
    editButtonVariant,
    errorMessage
  } = t0;
  const xs = t1 === undefined ? 8 : t1;
  const isSaveDisabled = t2 === undefined ? false : t2;
  const {
    currentUser
  } = useAuth();
  const {
    currentClient
  } = useCurrentClient();
  const {
    data: member
  } = useQueryMember(currentClient.id, currentUser?.id);
  const hasEditAccess = useHasAccess("editor", member?.role ?? "viewer");
  const editMode = editModes?.[name];
  let t3;
  if ($[0] !== canEdit || $[1] !== control || $[2] !== currentValue || $[3] !== defaultValue || $[4] !== editButtonVariant || $[5] !== editIcon || $[6] !== editLabel || $[7] !== editMode || $[8] !== errorMessage || $[9] !== handleUpdate || $[10] !== hasEditAccess || $[11] !== helperText || $[12] !== isCreateEvent || $[13] !== isSaveDisabled || $[14] !== label || $[15] !== labelChip || $[16] !== labelIcon || $[17] !== name || $[18] !== onCancelEdit || $[19] !== render || $[20] !== renderLabel || $[21] !== renderSecondaryLabel || $[22] !== rules || $[23] !== selectedLabel || $[24] !== toggleEdit || $[25] !== xs) {
    t3 = isCreateEvent && canEdit !== false || editMode === true ? <Grid xs={xs} display="flex" flexDirection="column" gap="16px">{label != null && <label htmlFor={name}><FormLabel labelText={label} labelChip={labelChip} required={rules?.required === true} iconUrl={labelIcon} secondaryLabel={helperText} renderLabel={renderLabel} renderSecondaryLabel={renderSecondaryLabel} /></label>}<Controller name={name} control={control} defaultValue={defaultValue} rules={rules} render={props => {
        const onBlur = () => {
          if (typeof props.field.value === "string") {
            props.field.onChange((props.field.value as string).trim());
          }
          props.field?.onBlur();
        };
        return render({
          ...props,
          field: {
            ...props.field,
            onBlur
          }
        });
      }} />{!isCreateEvent && canEdit === true && <FormButtons isSaveDisabled={isSaveDisabled} onSave={() => {
        handleUpdate?.(name, () => {
          toggleEdit?.(name);
        });
      }} onCancel={() => {
        onCancelEdit?.();
        toggleEdit?.(name);
      }} />}</Grid> : <EditItem label={<FormLabel labelText={selectedLabel ?? label ?? ""} iconUrl={labelIcon} />} canEdit={canEdit} hasEditAccess={hasEditAccess} currentValue={currentValue} onEditClick={() => {
      toggleEdit?.(name);
    }} editLabel={editLabel} editIcon={editIcon} editButtonVariant={editButtonVariant} errorMessage={errorMessage} />;
    $[0] = canEdit;
    $[1] = control;
    $[2] = currentValue;
    $[3] = defaultValue;
    $[4] = editButtonVariant;
    $[5] = editIcon;
    $[6] = editLabel;
    $[7] = editMode;
    $[8] = errorMessage;
    $[9] = handleUpdate;
    $[10] = hasEditAccess;
    $[11] = helperText;
    $[12] = isCreateEvent;
    $[13] = isSaveDisabled;
    $[14] = label;
    $[15] = labelChip;
    $[16] = labelIcon;
    $[17] = name;
    $[18] = onCancelEdit;
    $[19] = render;
    $[20] = renderLabel;
    $[21] = renderSecondaryLabel;
    $[22] = rules;
    $[23] = selectedLabel;
    $[24] = toggleEdit;
    $[25] = xs;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  return t3;
};
const StyledItem = styled(Grid)(() => ({
  wordBreak: 'break-word'
}));
const StyledErrorMessage = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '6px',
  gap: '6px',
  color: theme.tokens.foreground.main.primary
}));
export default FormItem;
function _temp(value) {
  if (isValidElement(value)) {
    const elementKey = value.key ?? `element-${String(value)}`;
    return <React.Fragment key={elementKey}>{value}</React.Fragment>;
  }
  return <Chip key={String(value)} label={value} />;
}